import React, { useEffect, useState } from 'react';
import { Loader2, Save, Eye, RefreshCw, Pin, Ban } from 'lucide-react';
import { adminGetExplorerSettings, adminPutExplorerSettings, adminExplorerPreview } from '../../services/cms';
import { useToast } from '../../hooks/use-toast';

const ExplorerSelectionPanel = () => {
  const [s, setS] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadPreview = async () => { try { setPreview(await adminExplorerPreview()); } catch (e) { setPreview(null); } };
  useEffect(() => { adminGetExplorerSettings().then(setS).catch(() => setS({ enabled: true, count: 6, mode: 'daily', excluded_ids: [], pinned_ids: [] })); loadPreview(); }, []);

  const save = async () => {
    if (!window.confirm('Enregistrer les réglages de la sélection Explorer ?')) return;
    setLoading(true);
    try { await adminPutExplorerSettings(s); toast({ title: 'Réglages enregistrés' }); await loadPreview(); }
    finally { setLoading(false); }
  };

  const togglePin = (id) => setS({ ...s, pinned_ids: s.pinned_ids.includes(id) ? s.pinned_ids.filter((x) => x !== id) : [...s.pinned_ids, id], excluded_ids: s.excluded_ids.filter((x) => x !== id) });
  const toggleExclude = (id) => setS({ ...s, excluded_ids: s.excluded_ids.includes(id) ? s.excluded_ids.filter((x) => x !== id) : [...s.excluded_ids, id], pinned_ids: s.pinned_ids.filter((x) => x !== id) });

  if (!s) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5" data-testid="explorer-selection-panel">
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-800 font-600">Sélection « À découvrir aujourd’hui » de la page Explorer. Aléatoire stable par jour (Europe/Paris). Épinglez ou excluez des webtoons sans jamais modifier Citytoon/Toocheke.</div>

      <div className="bg-card border border-border rounded-2xl p-4 grid sm:grid-cols-3 gap-4 items-end">
        <label className="flex items-center gap-2 cursor-pointer font-800 text-sm"><input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="w-5 h-5 accent-[#ed5c1f]" /> Aléatoire activé</label>
        <div><label className="text-xs font-800 uppercase text-muted-foreground block mb-1">Nombre de cartes</label>
          <input type="number" min="2" max="12" value={s.count} onChange={(e) => setS({ ...s, count: parseInt(e.target.value) || 6 })} className="w-full px-3 py-2 rounded-lg bg-muted font-700 outline-none" /></div>
        <div><label className="text-xs font-800 uppercase text-muted-foreground block mb-1">Mode</label>
          <select value={s.mode} onChange={(e) => setS({ ...s, mode: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-muted font-700 outline-none cursor-pointer"><option value="daily">Quotidien stable</option><option value="session">Par session</option><option value="manual">Manuel</option></select></div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={loading} data-testid="explorer-settings-save" className="flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-white font-800 text-sm disabled:opacity-60">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</button>
        <button onClick={loadPreview} className="flex items-center gap-2 px-5 py-2 rounded-full bg-muted font-800 text-sm hover:bg-secondary/40"><RefreshCw className="w-4 h-4" /> Rafraîchir l’aperçu</button>
        {preview && <span className="text-sm font-700 text-muted-foreground">Éligibles : {preview.eligible_total} · Exclus : {preview.excluded_count} · Jour : {preview.day}</span>}
      </div>

      {preview && (
        <div>
          <h3 className="font-display font-700 text-lg mb-3 flex items-center gap-2"><Eye className="w-5 h-5 text-primary" /> Sélection du jour (aperçu)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {preview.today.map((e) => {
              const pinned = s.pinned_ids.includes(e.source_id); const excluded = s.excluded_ids.includes(e.source_id);
              return (
                <div key={e.source_id} className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`explorer-preview-${e.slug}`}>
                  <div className="aspect-square overflow-hidden">{e.featured_image?.url ? <img src={e.featured_image.url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted" />}</div>
                  <div className="p-2">
                    <p className="text-xs font-700 line-clamp-2 min-h-[32px]">{e.titre}</p>
                    <p className="text-[10px] text-muted-foreground font-700 truncate">{e.project}</p>
                    <div className="flex gap-1 mt-1">
                      <button onClick={() => togglePin(e.source_id)} className={`flex-1 py-1 rounded-md text-[10px] font-800 flex items-center justify-center gap-1 ${pinned ? 'bg-primary text-white' : 'bg-muted'}`}><Pin className="w-3 h-3" /> Épingler</button>
                      <button onClick={() => toggleExclude(e.source_id)} className={`flex-1 py-1 rounded-md text-[10px] font-800 flex items-center justify-center gap-1 ${excluded ? 'bg-destructive text-white' : 'bg-muted'}`}><Ban className="w-3 h-3" /> Exclure</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {(s.pinned_ids.length > 0 || s.excluded_ids.length > 0) && (
            <p className="text-xs font-700 text-muted-foreground mt-3">Épinglés : {s.pinned_ids.length} · Exclus : {s.excluded_ids.length} (enregistrez pour appliquer)</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ExplorerSelectionPanel;
